import { normalizeReviewDate } from './mapBeerReviewDoc';

export const CHECKIN_TYPE_BREWERY = 'brewery';
export const CHECKIN_TYPE_BEER = 'beer';
export const CHECKIN_TYPE_EVENT = 'event';

export const CHECKIN_TYPE_LABELS = {
  [CHECKIN_TYPE_BREWERY]: 'Brewery visit logged',
  [CHECKIN_TYPE_BEER]: 'Beer logged',
  [CHECKIN_TYPE_EVENT]: 'Event logged',
};

const UNKNOWN_BEER = 'Unknown Beer';
const UNKNOWN_BREWERY = 'Unknown Brewery';
const UNKNOWN_EVENT = 'Unknown event';
const UNKNOWN_USER = 'Unknown User';

/**
 * Normalizes a Firestore check-in type string.
 * @param {unknown} rawType
 * @returns {'brewery' | 'beer' | 'event'}
 */
export function normalizeCheckinType(rawType) {
  const normalized = typeof rawType === 'string' ? rawType.trim().toLowerCase() : '';
  if (normalized === CHECKIN_TYPE_BEER) {
    return CHECKIN_TYPE_BEER;
  }
  if (normalized === CHECKIN_TYPE_EVENT) {
    return CHECKIN_TYPE_EVENT;
  }
  return CHECKIN_TYPE_BREWERY;
}

/**
 * @param {'brewery' | 'beer' | 'event'} type
 * @returns {string}
 */
export function getCheckinTypeLabel(type) {
  return CHECKIN_TYPE_LABELS[type] ?? CHECKIN_TYPE_LABELS[CHECKIN_TYPE_BREWERY];
}

/**
 * Maps a check-in Firestore document plus denormalized names to CheckinCard shape.
 * @param {object} params
 * @param {string} params.checkinId
 * @param {object} params.checkinData
 * @param {string} [params.beerName]
 * @param {string} [params.eventName]
 * @param {string} [params.breweryName]
 * @param {string} [params.breweryLocationLabel]
 * @param {string} [params.userName]
 * @returns {object}
 */
export function mapCheckinDoc({
  checkinId,
  checkinData,
  beerName = UNKNOWN_BEER,
  eventName = UNKNOWN_EVENT,
  breweryName = UNKNOWN_BREWERY,
  breweryLocationLabel,
  userName = UNKNOWN_USER,
}) {
  const safeCheckinData =
    checkinData && typeof checkinData === 'object' ? checkinData : {};
  const safeCheckinId = typeof checkinId === 'string' ? checkinId : '';
  const type = normalizeCheckinType(safeCheckinData.type);
  const typeLabel = getCheckinTypeLabel(type);

  const rawBeerId = safeCheckinData.beerId;
  const beerId = typeof rawBeerId === 'string' && rawBeerId.trim().length > 0
    ? rawBeerId.trim()
    : undefined;

  const rawEventId = safeCheckinData.eventId;
  const eventId = typeof rawEventId === 'string' && rawEventId.trim().length > 0
    ? rawEventId.trim()
    : undefined;

  const rawBreweryId = safeCheckinData.breweryId;
  const breweryId = typeof rawBreweryId === 'string' && rawBreweryId.trim().length > 0
    ? rawBreweryId.trim()
    : undefined;

  const userId = safeCheckinData.uid;
  const date = normalizeReviewDate(safeCheckinData.date);

  const rawRating = safeCheckinData.personalRating;
  const personalRating = typeof rawRating === 'number' && Number.isFinite(rawRating)
    ? rawRating
    : undefined;

  const rawNotes = safeCheckinData.privateNotes;
  const privateNotes = typeof rawNotes === 'string' && rawNotes.trim().length > 0
    ? rawNotes.trim()
    : undefined;

  const resolvedBreweryLocationLabel = typeof breweryLocationLabel === 'string'
    && breweryLocationLabel.trim().length > 0
    ? breweryLocationLabel.trim()
    : breweryName;

  let primaryTitle = breweryName;
  if (type === CHECKIN_TYPE_BEER) {
    primaryTitle = beerName;
  } else if (type === CHECKIN_TYPE_EVENT) {
    primaryTitle = eventName;
  } else if (resolvedBreweryLocationLabel.length > 0) {
    primaryTitle = resolvedBreweryLocationLabel;
  }

  return {
    id: safeCheckinId,
    type,
    typeLabel,
    beerId,
    beerName,
    eventId,
    eventName,
    breweryId,
    breweryName,
    breweryLocationLabel: resolvedBreweryLocationLabel,
    primaryTitle,
    userId,
    userName,
    date,
    personalRating,
    privateNotes,
  };
}
